import type { ModeId } from "./engine";

// One statement out of the editor, with the line it began on so a failure can be pointed back at the document.
export interface Statement {
  sql: string;
  line: number;
}

// Everything in the editor, cut at the semicolons that actually end a statement. The engines are handed one statement
// at a time rather than the whole panel, because a log with a time and a row count per statement is the point of the
// second tab and neither driver reports either for a batch. Comments and whitespace between statements ride along in
// the text of the one that follows them, but never in its line, which is where its own first character is.
export function splitStatements(text: string, dialect: ModeId): Statement[] {
  const statements: Statement[] = [];
  let start = 0;
  let line = 1;
  let startLine = 1;
  // Whether anything but comments and whitespace has been read since the last cut.
  let content = false;
  // BEGIN and CASE blocks still open inside a trigger body, where a semicolon separates the body's own statements.
  let depth = 0;
  let trigger = false;
  let head = "";

  // The line a statement is reported on is the line its first real character is on, which is where somebody looking
  // for what failed would look — a comment above it rides along in the text but does not drag the number up to itself.
  const mark = () => {
    if (content) return;
    content = true;
    startLine = line;
  };

  const cut = (end: number) => {
    const sql = text.slice(start, end).trim();
    // A run of comments and semicolons is not a statement, and handing one to a database says nothing to anybody.
    if (content && sql) statements.push({ sql, line: startLine });
    start = end;
    content = false;
    depth = 0;
    trigger = false;
    head = "";
  };

  for (let i = 0; i < text.length;) {
    const char = text[i];

    if (char === "\n") {
      i++;
      line++;
      continue;
    }

    if (char === "-" && text[i + 1] === "-") {
      i = skipTo(text, i + 2, "\n");
      continue;
    }

    if (char === "/" && text[i + 1] === "*") {
      const end = text.indexOf("*/", i + 2);
      const stop = end === -1 ? text.length : end + 2;
      line += countNewlines(text, i, stop);
      i = stop;
      continue;
    }

    // Postgres dollar quoting, which is what a function body is written between and the one quote a tag can name.
    const dollar = dialect === "postgres" ? DOLLAR_TAG.exec(text.slice(i)) : null;
    if (dollar) {
      mark();
      const tag = dollar[0];
      const end = text.indexOf(tag, i + tag.length);
      const stop = end === -1 ? text.length : end + tag.length;
      line += countNewlines(text, i, stop);
      i = stop;
      continue;
    }

    if (char === "'") {
      mark();
      // Only an E'' string reads a backslash as an escape; in every other string the quote is doubled instead.
      const escapes = dialect === "postgres" && ESCAPE_STRING.test(text.slice(Math.max(0, i - 2), i));
      const stop = skipQuoted(text, i, "'", escapes);
      line += countNewlines(text, i, stop);
      i = stop;
      continue;
    }

    if (char === "\"" || (dialect === "sqlite" && char === "`")) {
      mark();
      const stop = skipQuoted(text, i, char, false);
      line += countNewlines(text, i, stop);
      i = stop;
      continue;
    }

    // SQLite's other way of quoting an identifier, which has no doubling rule of its own.
    if (dialect === "sqlite" && char === "[") {
      mark();
      const end = text.indexOf("]", i + 1);
      const stop = end === -1 ? text.length : end + 1;
      line += countNewlines(text, i, stop);
      i = stop;
      continue;
    }

    if (char === ";") {
      i++;
      // A trigger body holds statements of its own, so only the semicolon after its END closes the CREATE.
      if (!(trigger && depth > 0)) cut(i);
      continue;
    }

    if (WORD_START.test(char)) {
      mark();
      let end = i + 1;
      while (end < text.length && WORD_PART.test(text[end])) end++;
      const word = text.slice(i, end).toLowerCase();
      i = end;

      if (trigger) {
        if (word === "begin" || word === "case") depth++;
        else if (word === "end") depth = Math.max(0, depth - 1);
      } else if (head.length < TRIGGER_HEAD) {
        head = head ? `${head} ${word}` : word;
        trigger = CREATE_TRIGGER.test(head);
      }
      continue;
    }

    if (!SPACE.test(char)) mark();
    i++;
  }

  cut(text.length);
  return statements;
}

// What the log calls a statement: one line of it, since a hundred-line insert is not a line of a log.
export function oneLine(sql: string, limit = 140): string {
  const flat = sql.replace(/\s+/g, " ").trim();
  return flat.length > limit ? `${flat.slice(0, limit - 1)}…` : flat;
}

// The command tag a database that reports none still has, which is the word the statement opens with.
export function commandOf(sql: string): string | null {
  const word = COMMAND.exec(sql);
  return word ? word[1].toUpperCase() : null;
}

function skipTo(text: string, from: number, needle: string): number {
  const end = text.indexOf(needle, from);
  return end === -1 ? text.length : end;
}

// Stops after the closing quote, having walked past every doubled one, which is how both dialects escape it.
function skipQuoted(text: string, from: number, quote: string, escapes: boolean): number {
  for (let i = from + 1; i < text.length; i++) {
    if (escapes && text[i] === "\\") {
      i++;
      continue;
    }
    if (text[i] !== quote) continue;
    if (text[i + 1] === quote) {
      i++;
      continue;
    }
    return i + 1;
  }
  return text.length;
}

function countNewlines(text: string, from: number, to: number): number {
  let count = 0;
  for (let i = from; i < to; i++) {
    if (text[i] === "\n") count++;
  }
  return count;
}

const DOLLAR_TAG = /^\$(?:[A-Za-z_\u0080-\uffff][A-Za-z0-9_\u0080-\uffff]*)?\$/;
const SPACE = /\s/;
const WORD_START = /[A-Za-z_\u0080-\uffff]/;
const WORD_PART = /[A-Za-z0-9_$\u0080-\uffff]/;
const COMMAND = /^(?:\s|--[^\n]*\n|\/\*[\s\S]*?\*\/)*([A-Za-z]+)/;

// The E of an E'' string, which is a token of its own rather than the tail of a word that happens to end in one.
const ESCAPE_STRING = /(?:^|[^A-Za-z0-9_$])[Ee]$/;

// Enough words to tell a CREATE TRIGGER from anything else opening with CREATE, and no more.
const TRIGGER_HEAD = 40;
const CREATE_TRIGGER = /^create(?: or replace)?(?: temp| temporary)? trigger$/;
